/**
 * TermsScreen — términos y condiciones de AetherLite (placeholder honesto).
 *
 * Esta pantalla se renderiza tanto dentro del shell autenticado (route `terms`
 * en App.tsx) como standalone si llega un visitante sin login (App.tsx
 * detecta `user === null && route === "terms"` y la monta sin shell con el
 * botón "Volver a inicio").
 *
 * El contenido es placeholder: redacción genérica pero seria, sin promesas
 * legales que no podamos cumplir. Para producción real se reemplaza por texto
 * revisado por abogado.
 */
interface TermsScreenProps {
  /** Si true, se muestra layout standalone (sin shell). */
  standalone?: boolean;
}

export function TermsScreen({ standalone }: TermsScreenProps = {}) {
  return (
    <div className={"al-terms-root " + (standalone ? "al-terms-standalone" : "")}>
      {standalone && <StandaloneHeader title="Términos y condiciones" />}

      <article className={standalone ? "al-terms-article-standalone" : "al-terms-article"}>
        {!standalone && <h1 className="al-terms-title">Términos y condiciones</h1>}

        <p className="al-terms-lead">
          Bienvenido a AetherLite, una plataforma operada por Nilabs que permite a
          entrenadores personales diseñar, gestionar y compartir planes de
          entrenamiento con sus clientes. Al usar el servicio aceptás los
          siguientes términos.
        </p>

        <Section title="1. Aceptación de los términos">
          <p>
            Al crear una cuenta en AetherLite o usar cualquier funcionalidad del
            sitio, declarás que leíste, entendiste y aceptaste estos términos en
            su totalidad. Si no estás de acuerdo con algún punto, no podés usar
            la plataforma.
          </p>
          <p>
            Estos términos pueden actualizarse periódicamente. La versión vigente
            es siempre la publicada en esta página, con la fecha de última
            actualización al pie. Los cambios sustanciales se comunican por
            email a los usuarios registrados con al menos 14 días de aviso.
          </p>
        </Section>

        <Section title="2. Uso del servicio">
          <p>
            AetherLite es una herramienta de productividad para profesionales del
            entrenamiento personal. Estás autorizado a usar la plataforma para
            crear planes destinados a clientes propios y compartirlos mediante
            los links de uso público que el sistema genera.
          </p>
          <p>
            No está permitido revender el servicio, hacer scraping del catálogo
            de ejercicios, intentar vulnerar la seguridad de la plataforma ni
            usarla para fines ilegales o que infrinjan derechos de terceros.
          </p>
          <p>
            AetherLite no sustituye el criterio profesional del entrenador. Sos
            el único responsable del contenido, la pertinencia y la seguridad de
            los planes que generes para tus clientes.
          </p>
        </Section>

        <Section title="3. Límites del plan contratado">
          <p>
            Cada plan (Free, Solo, Studio) tiene un límite específico de clientes
            activos, almacenamiento y funcionalidades habilitadas, descrito en la
            página de precios. Si excedés el límite de tu tier, podrás seguir
            consultando tus planes existentes pero no crear nuevos hasta hacer
            upgrade o liberar cupo desactivando clientes.
          </p>
          <p>
            El servicio se ofrece "tal cual" sin garantía explícita de
            disponibilidad ininterrumpida. Hacemos lo razonable para mantener un
            uptime alto y comunicar mantenimientos programados.
          </p>
        </Section>

        <Section title="4. Datos del cliente final">
          <p>
            Los datos personales de tus clientes (nombre, edad, mediciones, fotos
            que subas) son responsabilidad del entrenador. AetherLite actúa como
            encargado de tratamiento conforme la ley colombiana de Habeas Data
            (Ley 1581 de 2012) y normativa equivalente.
          </p>
          <p>
            Te comprometés a obtener el consentimiento explícito de tus clientes
            antes de cargar sus datos en la plataforma y a respetar sus derechos
            de acceso, rectificación y supresión cuando los soliciten.
          </p>
        </Section>

        <Section title="5. Pagos, cancelación y reembolsos">
          <p>
            Los planes pagos se cobran mensualmente por transferencia bancaria o
            PSE. La cancelación es efectiva al cierre del período facturado: no
            se generan cargos posteriores y mantenés acceso de lectura por 30
            días para descargar tu información.
          </p>
          <p>
            No se ofrecen reembolsos por períodos parciales. Si encontrás un
            problema técnico que te impide usar el servicio que pagaste,
            contactá soporte y resolvemos caso a caso.
          </p>
        </Section>

        <Section title="6. Propiedad intelectual">
          <p>
            La plataforma, su código, su diseño y el catálogo curado de
            ejercicios son propiedad de Nilabs o sus licenciantes. Los planes
            que vos creás te pertenecen: podés exportarlos a PDF y son
            descargables en cualquier momento.
          </p>
        </Section>

        <Section title="7. Modificaciones del servicio">
          <p>
            AetherLite es un producto en evolución. Podemos agregar, modificar o
            retirar funcionalidades, ajustar precios o cambiar la estructura de
            planes con aviso previo razonable. Nunca modificamos retroactivamente
            el contenido que vos hayas creado.
          </p>
        </Section>

        <Section title="8. Apps móviles y cliente final">
          <p>
            AetherLite ofrece apps móviles (iOS y Android): una para el
            entrenador y otra para su cliente final. El cliente accede mediante
            un enlace mágico que su entrenador le comparte; no requiere crear
            contraseña.
          </p>
          <p>
            El contenido de salud y fitness (planes, rutinas, dietas,
            recomendaciones generadas con IA) tiene fines informativos y de
            organización. No sustituye el consejo de un profesional médico. El
            entrenador es el responsable del criterio profesional aplicado a
            cada plan. Ante cualquier condición médica, el usuario debe consultar
            a un profesional de la salud.
          </p>
        </Section>

        <Section title="9. Limitación de responsabilidad">
          <p>
            En la máxima medida permitida por la ley aplicable, Nilabs no será
            responsable por daños indirectos, lucro cesante, pérdida de
            oportunidad ni daños derivados del uso o la imposibilidad de uso del
            servicio. La responsabilidad total agregada por cualquier reclamo se
            limita al equivalente de los pagos realizados en los últimos 12
            meses.
          </p>
        </Section>

        <Section title="10. Ley aplicable y contacto">
          <p>
            Estos términos se rigen por las leyes de la República de Colombia.
            Cualquier disputa se resolverá ante los tribunales competentes de la
            ciudad de Bogotá, salvo que la ley imperativa exija otra
            jurisdicción.
          </p>
          <p>
            Para dudas, reclamos o ejercer derechos sobre datos personales,
            escribinos a{" "}
            <a className="al-terms-link" href="mailto:nilton.alfonso@nilabs.co">
              nilton.alfonso@nilabs.co
            </a>
            .
          </p>
        </Section>

        <p className="al-terms-updated">
          Última actualización: Mayo 2026 · Para dudas:{" "}
          <a className="al-terms-link" href="mailto:nilton.alfonso@nilabs.co">
            nilton.alfonso@nilabs.co
          </a>
        </p>
      </article>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="al-terms-section">
      <h2 className="al-terms-h2">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Header simplificado para uso standalone (visitante sin login). Reusa los
 * estilos visuales mínimos: marca + botón volver. NO importa el shell completo
 * (sidebar, topbar, etc.).
 */
export function StandaloneHeader({ title }: { title: string }) {
  return (
    <header className="al-terms-standalone-header">
      <div className="al-terms-standalone-inner">
        <a href="/" className="al-terms-standalone-brand" aria-label="Volver al inicio">
          <span className="al-terms-standalone-mark">AL</span>
          <span className="al-terms-standalone-name">
            Aether<strong>Lite</strong>
          </span>
        </a>
        <a href="/" className="btn-secondary btn-sm al-terms-back">
          ← Volver al inicio
        </a>
      </div>
      <h1 className="al-terms-standalone-title">{title}</h1>
    </header>
  );
}
